/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { DescriptionPage_CourseType as DescriptionPageCourseType } from '__generated__/graphql-types';
import { compose } from 'recompose';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import connectToRouter from 'js/lib/connectToRouter';
import deferToClientSideRender from 'js/lib/deferToClientSideRender';

import { InlineNotification } from '@coursera/cds-core';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import withEnrollment from 'bundles/enroll-common/components/xdp/withEnrollment';
import type { PropsFromWithEnrollment } from 'bundles/enroll-common/components/xdp/withEnrollment';
import { scopes } from 'bundles/enroll-common/utils/scopedContentRule';
import type { PropsFromWithScopedContentRule } from 'bundles/enroll-common/utils/withScopedContentRule';
import withScopedContentRule from 'bundles/enroll-common/utils/withScopedContentRule';
import withEnrollModal from 'bundles/enroll-modal/components/withEnrollModal';
import Naptime from 'bundles/naptimejs';
import CoursesV1 from 'bundles/naptimejs/resources/courses.v1';
import withPromotionInfo from 'bundles/promotions/components/withPromotionInfo';
import { useUndoCancellationDataForXdp } from 'bundles/subscriptions-common/utils/cancellationUndoUtils';
import EnrollButtonContent from 'bundles/unified-description-page-common/components/enroll/consumer/EnrollButtonContent';
import { useCourseraPlusOwnership } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';
import EnrollButtonViewController from 'bundles/unified-description-page-common/data/controllers/EnrollButtonViewController';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';

import _t from 'i18n!nls/xdp';

type ProjectEnrollButtonSize = 'small' | 'large';

const styles = {
  buttonOverrides: (size?: ProjectEnrollButtonSize) => css`
    min-height: ${size === 'small' ? '36px' : '48px'};

    .with-enroll-modal {
      display: block;
    }

    .dual-cta-subtext {
      font-size: 0.8rem;

      .rc-StartDateString {
        line-height: inherit;
      }
    }
  `,
};

const EnrollmentButton = withEnrollModal(EnrollButtonContent);

type PropsFromCaller = {
  courseId: string;
  courseType: DescriptionPageCourseType;
  // Used to check showing the enroll modal after logging in
  shouldCheckActionParams: boolean;
  size?: ProjectEnrollButtonSize;
  onlyShowButton?: boolean;
  productDisplayName: string;
};

type PropsFromRouter = {
  router: InjectedRouter;
};

type PropsFromNaptime = {
  course: CoursesV1;
};

type PropsToDataWrapper = PropsFromCaller &
  PropsFromRouter &
  PropsFromWithEnrollment &
  PropsFromNaptime &
  PropsFromWithScopedContentRule;

const toEnrollButtonViewCourseType = (courseType: DescriptionPageCourseType) => {
  switch (courseType) {
    case 'PROJECT': {
      return 'Project';
    }
    case 'RHYME_PROJECT': {
      return 'GuidedProject';
    }
    default: {
      return 'StandardCourse';
    }
  }
};

const DataWrapper: React.FC<PropsToDataWrapper> = ({
  enrollmentAvailableChoices,
  course,
  scopedContentRule,
  courseType,
  size,
  onlyShowButton,
  productDisplayName,
  ...rest
}) => {
  const {
    showPlusMonthlyToAnnualUpgradeButton,
    isEligibleForPlusMonthlyToAnnualUpgrade,
    isCourseraPlusEligibleProduct,
  } = useCourseraPlusOwnership() ?? {};
  const { showUndoCancellationCTA } = useUndoCancellationDataForXdp({ isCourseraPlusEligibleProduct });
  const enrollButtonViewController = new EnrollButtonViewController(
    toEnrollButtonViewCourseType(courseType),
    course,
    enrollmentAvailableChoices,
    scopedContentRule ?? undefined,
    productDisplayName
  );
  const propsFromEnrollButtonViewController = enrollButtonViewController.getButtonProps();
  const props = {
    ...propsFromEnrollButtonViewController,
    ...rest,
    onStickyHeader: size === 'small',
    ...(showPlusMonthlyToAnnualUpgradeButton || showUndoCancellationCTA
      ? {
          buttonLabel: (
            <div>
              <span>{propsFromEnrollButtonViewController.buttonLabel}</span>
              <div className="dual-cta-subtext">
                {propsFromEnrollButtonViewController.isEnrolled
                  ? _t('Already enrolled')
                  : _t('Included with Coursera Plus')}
              </div>
            </div>
          ),
        }
      : {}),
  };

  return (
    <div css={styles.buttonOverrides(size)}>
      <EnrollmentButton
        {...props}
        shouldCheckActionParams={!onlyShowButton}
        isEligibleForPlusMonthlyToAnnualUpgrade={Boolean(isEligibleForPlusMonthlyToAnnualUpgrade)}
        isCourseraPlusEligibleProduct={isCourseraPlusEligibleProduct}
        showUndoCancellationCTA={showUndoCancellationCTA}
      />
    </div>
  );
};

const WrappedConsumerProjectEnrollButton = compose<PropsToDataWrapper, PropsFromCaller>(
  deferToClientSideRender,
  connectToRouter<PropsFromRouter, PropsFromCaller>((router) => ({ router })),
  withEnrollment(true),
  withPromotionInfo(),
  Naptime.createContainer<PropsFromNaptime, PropsFromCaller>(({ courseId }) => ({
    course: CoursesV1.get(courseId, {
      fields: ['courseStatus'],
      params: {
        showHidden: true,
      },
    }),
  })),
  withScopedContentRule<PropsFromCaller>(scopes.COURSE_WITH_FULL_DISCOUNT)
)(DataWrapper);

const ConsumerProjectEnrollButtonNotConfigurable = ({
  pageData,
  size,
  onlyShowButton,
  productDisplayName,
}: {
  pageData: ConsumerCoursePageFieldsFragment;
  size?: ProjectEnrollButtonSize;
  onlyShowButton?: boolean;
  productDisplayName: string;
}) => {
  if (!pageData) {
    return null;
  }

  return pageData.courseType ? (
    <ErrorBoundaryWithTagging
      componentName="ConsumerEnrollButtonNotConfigurable"
      errorComponent={
        onlyShowButton ? undefined : (
          <InlineNotification
            severity="error"
            title={_t('An error occurred within our enrollment service')}
            role="alert"
          >
            {_t('Our development team has been informed and is actively working towards a resolution.')}
          </InlineNotification>
        )
      }
    >
      <WrappedConsumerProjectEnrollButton
        courseId={pageData.id}
        courseType={pageData.courseType}
        shouldCheckActionParams={true}
        size={size}
        onlyShowButton={onlyShowButton}
        productDisplayName={productDisplayName}
      />
    </ErrorBoundaryWithTagging>
  ) : null;
};

export default ConsumerProjectEnrollButtonNotConfigurable;
